import type {
  GeoJsonFeatureCollection,
  MapPointCloud,
} from '@gpx-spa/geo-domain';
import {
  buildTrackPointCloudFromDecodedRows,
  type TrackPointCloudDecodedRow,
} from '@/map/operational/build-track-point-cloud';
import { lineColorHexForShipmentDevice } from '@/map/operational/shipment-status-line-colors';

export type RawDeviceTracksDecodedRow = TrackPointCloudDecodedRow;

export type RawDeviceTracksDisplay = {
  lineCollection: GeoJsonFeatureCollection;
  pointCloud: MapPointCloud;
};

/** Short dash so raw paths read distinctly from solid convoy track. */
const RAW_DEVICE_TRACK_DASH = [2, 1.5] as const;

/**
 * Per-device raw decoded lines + point cloud (full pings, not convoy vertices).
 */
export function buildRawDeviceTracksDisplay(
  decodedRows: readonly RawDeviceTracksDecodedRow[],
  status: string | undefined,
): RawDeviceTracksDisplay {
  const sorted = [...decodedRows].sort((a, b) => a.device_id - b.device_id);
  const features: GeoJsonFeatureCollection['features'] = [];

  sorted.forEach((row, deviceIndex) => {
    const decoded = row.decoded;
    if (!decoded?.coordinates || decoded.coordinates.length < 2) return;
    const lineColorHex = lineColorHexForShipmentDevice(status, deviceIndex);
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: decoded.coordinates,
      },
      properties: {
        map: {
          lineColor: lineColorHex,
          lineWidth: 2,
          lineOpacity: 0.7,
          lineDashArray: [...RAW_DEVICE_TRACK_DASH],
        },
        rawDevice: {
          deviceId: row.device_id,
          colorIndex: deviceIndex,
        },
      },
    });
  });

  return {
    lineCollection: { type: 'FeatureCollection', features },
    pointCloud: buildTrackPointCloudFromDecodedRows(
      decodedRows,
      status,
      'per-device',
    ),
  };
}

/** Merge raw line collections across shipments. */
export function mergeRawDeviceTrackLineCollections(
  collections: readonly GeoJsonFeatureCollection[],
): GeoJsonFeatureCollection {
  const features: GeoJsonFeatureCollection['features'] = [];
  for (const c of collections) {
    features.push(...c.features);
  }
  return { type: 'FeatureCollection', features };
}
